import type {
  GraphEdgeV5,
  GraphEntityType,
  GraphNodeV5,
} from '@api/api/ontology';

import type { FilterType, ThemeType } from './graph-types';

export const getThemeType = (node: GraphNodeV5): ThemeType =>
  node.attributes.sub_type ?? 'industry';

export const parseGraphNodeKey = (
  key: string
): { type: GraphEntityType; rawId: string } => {
  const separator = key.indexOf('/');

  return {
    type: key.slice(0, separator) as GraphEntityType,
    rawId: key.slice(separator + 1),
  };
};

export const nodeKeyOf = (type: GraphEntityType, rawId: string) =>
  `${type}/${rawId}`;

export const nodeDisplayLabel = (node: GraphNodeV5) =>
  node.attributes.label ?? parseGraphNodeKey(node.key).rawId;

// Grapheme-aware first letter (handles surrogate pairs) for letter-fallback icons.
export const nodeInitial = (label: string) =>
  Array.from(label.trim())[0]?.toUpperCase() ?? '';

// source/target are node keys; a node's connection is the opposite endpoint.
// Returns null when the edge is not incident to nodeKey.
export const oppositeEndpoint = (
  edge: Pick<GraphEdgeV5, 'source' | 'target'>,
  nodeKey: string | null
): string | null => {
  if (!nodeKey) {
    return null;
  }
  if (edge.source === nodeKey) {
    return edge.target;
  }
  if (edge.target === nodeKey) {
    return edge.source;
  }
  return null;
};

// Direction-agnostic key so A→B and B→A collide in a Set.
export const pairKey = (edge: Pick<GraphEdgeV5, 'source' | 'target'>) =>
  [edge.source, edge.target].sort().join('_');

// The graph's focus anchor: last activated node, else the most recent
// expansion, else the primary. Shared by the selector (store-derived key) and
// the view (URL-derived key) so both agree on "current focus".
export const resolveFocusNodeKey = (focus: {
  activeNodeKey: string | null;
  expandedNodeKeys: readonly string[];
  primaryNodeKey: string | null;
}): string | null =>
  focus.activeNodeKey ?? focus.expandedNodeKeys.at(-1) ?? focus.primaryNodeKey;

export const getNodeFilterType = (node: GraphNodeV5): FilterType => {
  if (node.attributes.type !== 'theme') {
    return node.attributes.type;
  }

  return `${getThemeType(node)}-theme`;
};

export const getNodeTickerAndExchange = (node?: GraphNodeV5) => {
  if (!node) {
    return '';
  }

  if (node.attributes.exchange && node.attributes.ticker) {
    return `${node.attributes.ticker}:${node.attributes.exchange?.toUpperCase()}`;
  }

  return node.attributes.ticker ?? '';
};
